import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, User, Mail, Phone, Calendar, Briefcase, MapPin, CreditCard, Award, Clock, Droplet, Users, ShieldAlert, GraduationCap, Target, Pencil, Save, X, Camera } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const EmployeeProfile = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [employeeData, setEmployeeData] = useState(null);
    const [profileUser, setProfileUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editData, setEditData] = useState({});
    const fileInputRef = useRef(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    useEffect(() => {
        fetchEmployeeProfile();
    }, []);

    const fetchEmployeeProfile = async () => {
        try {
            setLoading(true);
            const data = await api.getCurrentUser();
            setEmployeeData(data.employee);
            setProfileUser(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast({
                title: 'Error',
                description: 'Failed to load profile',
                variant: 'destructive'
            });
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const startEditing = () => {
        setEditData({
            faceReferenceUrl: null,
            currentAddress: employeeData?.currentAddress || '',
            permanentAddress: employeeData?.permanentAddress || '',
            city: employeeData?.city || '',
            state: employeeData?.state || '',
            pinCode: employeeData?.pinCode || '',
            paymentMode: employeeData?.paymentMode || '',
            bankName: employeeData?.bankName || '',
            bankBranch: employeeData?.bankBranch || '',
            accountNumber: employeeData?.accountNumber || '',
            ifscCode: employeeData?.ifscCode || '',
            panNumber: employeeData?.panNumber || '',
            aadharNumber: employeeData?.aadharNumber || '',
        });
        setPhotoPreview(null);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditData({});
        setPhotoPreview(null);
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast({ title: 'Error', description: 'Please select an image file', variant: 'destructive' });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast({ title: 'Error', description: 'Image must be less than 5MB', variant: 'destructive' });
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
            setPhotoPreview(event.target.result);
            setEditData(prev => ({ ...prev, faceReferenceUrl: event.target.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        // Validate fields
        const errors = [];

        if (editData.pinCode && !/^\d{6}$/.test(editData.pinCode)) {
            errors.push('Pin Code must be exactly 6 digits');
        }

        if (editData.accountNumber && !/^\d{9,18}$/.test(editData.accountNumber)) {
            errors.push('Account Number must be 9-18 digits');
        }

        if (editData.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(editData.ifscCode.toUpperCase())) {
            errors.push('IFSC Code must be in format: ABCD0XXXXXX');
        }

        if (editData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(editData.panNumber.toUpperCase())) {
            errors.push('PAN Number must be in format: ABCDE1234F');
        }

        if (editData.aadharNumber && !/^\d{12}$/.test(editData.aadharNumber.replace(/\s/g, ''))) {
            errors.push('Aadhar Number must be exactly 12 digits');
        }

        if (errors.length > 0) {
            toast({ title: 'Validation Error', description: errors.join('. '), variant: 'destructive' });
            return;
        }

        try {
            setSaving(true);
            const payload = { ...editData };
            // Normalize uppercase for IDs
            if (payload.ifscCode) payload.ifscCode = payload.ifscCode.toUpperCase();
            if (payload.panNumber) payload.panNumber = payload.panNumber.toUpperCase();
            if (payload.aadharNumber) payload.aadharNumber = payload.aadharNumber.replace(/\s/g, '');
            // Only send faceReferenceUrl if a new photo was chosen
            if (!payload.faceReferenceUrl) {
                delete payload.faceReferenceUrl;
            }
            await api.updateSelfProfile(payload);
            toast({ title: 'Success', description: 'Profile updated successfully' });
            setIsEditing(false);
            setPhotoPreview(null);
            fetchEmployeeProfile();
        } catch (error) {
            console.error('Error updating profile:', error);
            toast({ title: 'Error', description: error.message || 'Failed to update profile', variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
                            <p className="text-gray-600">{isEditing ? 'Edit your profile information' : 'View your employee information'}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {isEditing ? (
                            <>
                                <Button variant="outline" onClick={cancelEditing} disabled={saving}>
                                    <X className="w-4 h-4 mr-1" /> Cancel
                                </Button>
                                <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                                </Button>
                            </>
                        ) : (
                            <Button onClick={startEditing} className="bg-teal-600 hover:bg-teal-700">
                                <Pencil className="w-4 h-4 mr-1" /> Edit Profile
                            </Button>
                        )}
                    </div>
                </div>

                {/* Profile Photo Card */}
                <Card className="mb-6">
                    <CardContent className="p-6">
                        <div className="flex flex-col items-center">
                            {isEditing ? (
                                <div className="relative">
                                    {photoPreview || employeeData?.faceReferenceUrl ? (
                                        <img
                                            src={photoPreview || employeeData.faceReferenceUrl}
                                            alt="Profile"
                                            className="w-32 h-32 rounded-full object-cover border-4 border-teal-500"
                                        />
                                    ) : (
                                        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                                            <User className="w-16 h-16 text-white" />
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="absolute bottom-0 right-0 w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-teal-700"
                                    >
                                        <Camera className="w-5 h-5" />
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="user"
                                        className="hidden"
                                        onChange={handlePhotoChange}
                                    />
                                </div>
                            ) : (
                                employeeData?.faceReferenceUrl ? (
                                    <img
                                        src={employeeData.faceReferenceUrl}
                                        alt="Profile"
                                        className="w-32 h-32 rounded-full object-cover border-4 border-teal-500"
                                    />
                                ) : (
                                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                                        <User className="w-16 h-16 text-white" />
                                    </div>
                                )
                            )}
                            <h2 className="mt-4 text-2xl font-bold text-gray-900">{profileUser?.name || user.name}</h2>
                            <p className="text-gray-600">{employeeData?.employeeCode || 'N/A'}</p>
                            <div className="mt-2">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                                    {profileUser?.role || user.role}
                                </span>
                            </div>
                            {isEditing && <p className="mt-2 text-sm text-gray-500">Tap the camera icon to take a selfie</p>}
                        </div>
                    </CardContent>
                </Card>

                {/* Basic Information */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                        <CardDescription>Your personal and contact details</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="font-medium text-gray-900">{profileUser?.email || user.email || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                    <Phone className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Phone</p>
                                    <p className="font-medium text-gray-900">{profileUser?.phone || user.phone || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Employment Details */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Employment Details</CardTitle>
                        <CardDescription>Your job and work information</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Briefcase className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Designation</p>
                                    <p className="font-medium text-gray-900">{employeeData?.designation || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                                    <Briefcase className="w-5 h-5 text-cyan-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Department</p>
                                    <p className="font-medium text-gray-900">{employeeData?.department || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <Award className="w-5 h-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Skill Level</p>
                                    <p className="font-medium text-gray-900 capitalize">{employeeData?.skillLevel || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-teal-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Joining Date</p>
                                    <p className="font-medium text-gray-900">{formatDate(employeeData?.joiningDate)}</p>
                                </div>
                            </div>



                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <Briefcase className="w-5 h-5 text-yellow-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Employment Type</p>
                                    <p className="font-medium text-gray-900">{employeeData?.employmentType || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Address Information */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Address Information</CardTitle>
                        <CardDescription>{isEditing ? 'Update your residential details' : 'Your residential details'}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {isEditing ? (
                            <div className="space-y-4">
                                <div>
                                    <Label>Current Address</Label>
                                    <Input
                                        value={editData.currentAddress}
                                        onChange={(e) => setEditData(prev => ({ ...prev, currentAddress: e.target.value }))}
                                        placeholder="Enter current address"
                                    />
                                </div>
                                <div>
                                    <Label>Permanent Address</Label>
                                    <Input
                                        value={editData.permanentAddress}
                                        onChange={(e) => setEditData(prev => ({ ...prev, permanentAddress: e.target.value }))}
                                        placeholder="Enter permanent address"
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label>City</Label>
                                        <Input
                                            value={editData.city}
                                            onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                                            placeholder="City"
                                        />
                                    </div>
                                    <div>
                                        <Label>State</Label>
                                        <Input
                                            value={editData.state}
                                            onChange={(e) => setEditData(prev => ({ ...prev, state: e.target.value }))}
                                            placeholder="State"
                                        />
                                    </div>
                                    <div>
                                        <Label>Pin Code</Label>
                                        <Input
                                            value={editData.pinCode}
                                            onChange={(e) => setEditData(prev => ({ ...prev, pinCode: e.target.value }))}
                                            placeholder="Pin Code"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-500">Current Address</p>
                                        <p className="font-medium text-gray-900">{employeeData?.currentAddress || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-500">Permanent Address</p>
                                        <p className="font-medium text-gray-900">{employeeData?.permanentAddress || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                    <div>
                                        <p className="text-sm text-gray-500">City</p>
                                        <p className="font-medium text-gray-900">{employeeData?.city || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">State</p>
                                        <p className="font-medium text-gray-900">{employeeData?.state || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Pin Code</p>
                                        <p className="font-medium text-gray-900">{employeeData?.pinCode || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Personal Details */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Personal Details</CardTitle>
                        <CardDescription>Your personal information</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                    <Droplet className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Blood Group</p>
                                    <p className="font-medium text-gray-900">{employeeData?.bloodGroup || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Skills & Experience */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Skills & Experience</CardTitle>
                        <CardDescription>Your professional qualifications and expertise</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Years of Experience</p>
                                    <p className="font-medium text-gray-900">
                                        {employeeData?.yearsOfExperience ? `${employeeData.yearsOfExperience} years` : 'N/A'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Target className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Specialization</p>
                                    <p className="font-medium text-gray-900">{employeeData?.specialization || 'N/A'}</p>
                                </div>
                            </div>

                            {employeeData?.certifications && (
                                <div className="md:col-span-2">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <GraduationCap className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-500">Certifications</p>
                                            <p className="font-medium text-gray-900">{employeeData.certifications}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Emergency Contact */}
                {(employeeData?.emergencyContactName || employeeData?.emergencyContactPhone) && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Emergency Contact</CardTitle>
                            <CardDescription>Contact person in case of emergency</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                        <ShieldAlert className="w-5 h-5 text-red-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Name</p>
                                        <p className="font-medium text-gray-900">{employeeData?.emergencyContactName || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-orange-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Relation</p>
                                        <p className="font-medium text-gray-900">{employeeData?.emergencyContactRelation || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                        <Phone className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Phone</p>
                                        <p className="font-medium text-gray-900">{employeeData?.emergencyContactPhone || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Bank & ID Details */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Payment & Bank Details</CardTitle>
                        <CardDescription>{isEditing ? 'Update your financial information' : 'Your financial and identification information'}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {isEditing ? (
                            <div className="space-y-4">
                                <div>
                                    <Label>Payment Mode</Label>
                                    <Select
                                        value={editData.paymentMode}
                                        onValueChange={(val) => setEditData(prev => ({ ...prev, paymentMode: val }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select payment mode" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                            <SelectItem value="upi">UPI</SelectItem>
                                            <SelectItem value="cash">Cash</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Bank Account Details</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label>Bank Name</Label>
                                            <Input
                                                value={editData.bankName}
                                                onChange={(e) => setEditData(prev => ({ ...prev, bankName: e.target.value }))}
                                                placeholder="Bank Name"
                                            />
                                        </div>
                                        <div>
                                            <Label>Branch</Label>
                                            <Input
                                                value={editData.bankBranch}
                                                onChange={(e) => setEditData(prev => ({ ...prev, bankBranch: e.target.value }))}
                                                placeholder="Branch"
                                            />
                                        </div>
                                        <div>
                                            <Label>Account Number</Label>
                                            <Input
                                                value={editData.accountNumber}
                                                onChange={(e) => setEditData(prev => ({ ...prev, accountNumber: e.target.value }))}
                                                placeholder="Account Number"
                                            />
                                        </div>
                                        <div>
                                            <Label>IFSC Code</Label>
                                            <Input
                                                value={editData.ifscCode}
                                                onChange={(e) => setEditData(prev => ({ ...prev, ifscCode: e.target.value }))}
                                                placeholder="IFSC Code"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Government ID Details</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label>PAN Number</Label>
                                            <Input
                                                value={editData.panNumber}
                                                onChange={(e) => setEditData(prev => ({ ...prev, panNumber: e.target.value }))}
                                                placeholder="PAN Number"
                                            />
                                        </div>
                                        <div>
                                            <Label>Aadhar Number</Label>
                                            <Input
                                                value={editData.aadharNumber}
                                                onChange={(e) => setEditData(prev => ({ ...prev, aadharNumber: e.target.value }))}
                                                placeholder="Aadhar Number"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                            <CreditCard className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Payment Mode</p>
                                            <p className="font-medium text-gray-900 capitalize">{employeeData?.paymentMode || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                {employeeData?.accountNumber && (
                                    <>
                                        <div className="border-t pt-4 mt-4">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Bank Account Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">Bank Name</p>
                                                    <p className="font-medium text-gray-900">{employeeData.bankName || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Branch</p>
                                                    <p className="font-medium text-gray-900">{employeeData.bankBranch || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Account Number</p>
                                                    <p className="font-medium text-gray-900">{employeeData.accountNumber}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">IFSC Code</p>
                                                    <p className="font-medium text-gray-900">{employeeData.ifscCode || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t pt-4 mt-4">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Government ID Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">PAN Number</p>
                                                    <p className="font-medium text-gray-900">{employeeData.panNumber || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Aadhar Number</p>
                                                    <p className="font-medium text-gray-900">
                                                        {employeeData.aadharNumber ? '****-****-' + employeeData.aadharNumber.slice(-4) : 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default EmployeeProfile;
